/**
 * Tính toán màu thuần cho bộ tuỳ chỉnh giao diện (ADR-UI-0016).
 *
 * Tách khỏi `styles/tuy-chinh.ts` để chạy được bằng Node trong test
 * (`tuy-chinh.ts` import `./tokens` không có đuôi). Tệp này **không import gì**.
 *
 * ## Vì sao cần `chinhChoDocDuoc`
 *
 * ADR-UI-0007 chỉ cho 8 màu chọn sẵn vì màu tự do cho phép chọn vàng chanh làm
 * màu nút, và chữ trắng trên đó không đọc được. Khi cho chọn màu tự do, thay vì
 * chặn người dùng, ta **làm đậm màu vừa đủ** cho tới khi ba chỗ màu nhấn chở
 * chữ đều đạt WCAG AA. Ba chỗ này là đúng ba chỗ `scripts/check-design.mjs`
 * (luật 6) kiểm cho 8 màu chọn sẵn; công thức phải giữ khớp với nó và với bản
 * sao trong script chặn FOUC ở `index.html`.
 */

type Rgb = [number, number, number];

/** Màu nền `surface` của chủ đề tối (`#0F172A`), dạng RGB để tệp này không chứa hex. */
const NEN_TOI: Rgb = [15, 23, 42];

/** Ngưỡng tương phản WCAG AA cho chữ thường. */
export const NGUONG_AA = 4.5;

const HEX_6 = /^#[0-9a-f]{6}$/i;

export function laHex(giaTri: unknown): giaTri is string {
  return typeof giaTri === 'string' && HEX_6.test(giaTri);
}

/** Chuẩn hoá về chữ in hoa để so sánh và lưu một dạng duy nhất. */
export function chuanHoaHex(hex: string): string {
  return hex.toUpperCase();
}

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export function rgbToHex([r, g, b]: Rgb): string {
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`.toUpperCase();
}

/** Trộn `rgb` về trắng hoặc đen; `t` = 1 là đích hẳn. */
function phaRgb(rgb: Rgb, t: number, dich: 'trang' | 'den'): Rgb {
  const d = dich === 'trang' ? 255 : 0;
  return [rgb[0] + (d - rgb[0]) * t, rgb[1] + (d - rgb[1]) * t, rgb[2] + (d - rgb[2]) * t];
}

/** Trộn một màu hex với trắng/đen. */
export function pha(hex: string, t: number, dich: 'trang' | 'den'): string {
  return rgbToHex(phaRgb(hexToRgb(hex), t, dich));
}

function sang(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function doSang(rgb: Rgb): number {
  return 0.2126 * sang(rgb[0]) + 0.7152 * sang(rgb[1]) + 0.0722 * sang(rgb[2]);
}

export function tuongPhan(a: Rgb, b: Rgb): number {
  const [cao, thap] = [doSang(a), doSang(b)].sort((x, y) => y - x);
  return (cao + 0.05) / (thap + 0.05);
}

/** Ghép màu có alpha lên một nền đục. */
function phuLen(rgb: Rgb, alpha: number, nen: Rgb): Rgb {
  return [
    rgb[0] * alpha + nen[0] * (1 - alpha),
    rgb[1] * alpha + nen[1] * (1 - alpha),
    rgb[2] * alpha + nen[2] * (1 - alpha),
  ];
}

/**
 * Ba chỗ màu nhấn chở chữ, cùng công thức với luật 6 của `check-design`:
 *
 * 1. Nút `primary`: chữ trắng trên `base`.
 * 2. Chip nền sáng: chữ bậc `-700` (pha 16% đen) trên nền `-50` (pha 95% trắng).
 * 3. Chip nền tối: chữ bậc `-700` (pha 54% trắng) trên nền rgba(.16) ghép lên surface.
 */
export function docDuocTrenMoiNen(hex: string): boolean {
  const rgb = hexToRgb(hex);
  const nut = tuongPhan([255, 255, 255], rgb);
  const chipSang = tuongPhan(phaRgb(rgb, 0.16, 'den'), phaRgb(rgb, 0.95, 'trang'));
  const chipToi = tuongPhan(phaRgb(rgb, 0.54, 'trang'), phuLen(rgb, 0.16, NEN_TOI));
  return nut >= NGUONG_AA && chipSang >= NGUONG_AA && chipToi >= NGUONG_AA;
}

export interface KetQuaChinhMau {
  /** Màu dùng thật, in hoa. */
  hex: string;
  /** `true` nếu phải làm đậm so với màu người dùng chọn. */
  daChinh: boolean;
}

/**
 * Làm đậm `hex` từng 2% cho tới khi đọc được trên mọi nền.
 *
 * Màu đã đạt thì trả nguyên. Màu rất sáng (vàng, trắng) cần làm đậm nhiều và
 * kết quả sẽ khác xa màu chọn; giao diện nên báo `daChinh` để người dùng biết.
 */
export function chinhChoDocDuoc(hex: string): KetQuaChinhMau {
  const goc = chuanHoaHex(hex);
  if (docDuocTrenMoiNen(goc)) return { hex: goc, daChinh: false };

  for (let buoc = 1; buoc <= 50; buoc++) {
    const thu = pha(goc, buoc * 0.02, 'den');
    if (docDuocTrenMoiNen(thu)) return { hex: thu, daChinh: true };
  }
  // Pha 100% đen luôn đạt: chữ trắng trên đen là 21:1.
  return { hex: pha(goc, 1, 'den'), daChinh: true };
}
